import React, { useState, useEffect, useRef } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import supportService from "../services/supportService";
import { MessageSquarePlus, Send, X, Paperclip, Clock, CheckCircle2, ChevronLeft, Image as ImageIcon } from "lucide-react";
import { format } from "date-fns";
import { useLocation } from "react-router-dom";

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTicket, setActiveTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Forms
  const [newSubject, setNewSubject] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyFile, setReplyFile] = useState(null);

  const messagesEndRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    loadTickets();
  }, []);

  // Sélection automatique via URL (ex: ?id=123)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ticketId = params.get('id');
    if (ticketId && tickets.length > 0) {
      const ticket = tickets.find(t => t.id.toString() === ticketId);
      if (ticket) setActiveTicket(ticket);
    }
  }, [tickets, location.search]);

  useEffect(() => {
    if (activeTicket) {
      loadMessages(activeTicket.id);
    }
  }, [activeTicket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadTickets = async () => {
    try {
      const res = await supportService.getUserTickets();
      if (res.success) setTickets(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (ticketId) => {
    try {
      const res = await supportService.getTicketMessages(ticketId);
      if (res.success) setMessages(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newSubject.trim() || !newMessage.trim()) return;

    try {
      const formData = new FormData();
      formData.append('subject', newSubject);
      formData.append('message', newMessage);
      if (selectedFile) formData.append('attachment', selectedFile);

      const res = await supportService.createTicket(formData);
      if (res.success) {
        setIsModalOpen(false);
        setNewSubject('');
        setNewMessage('');
        setSelectedFile(null);
        loadTickets();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyMessage.trim() && !replyFile) return;

    try {
      const formData = new FormData();
      if (replyMessage) formData.append('message', replyMessage);
      if (replyFile) formData.append('attachment', replyFile);

      const res = await supportService.replyToTicket(activeTicket.id, formData);
      if (res.success) {
        setReplyMessage('');
        setReplyFile(null);
        loadMessages(activeTicket.id);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'open': return <span className="px-2 py-1 bg-yellow-500/10 text-yellow-500 text-xs rounded-full font-bold uppercase tracking-wider">En attente</span>;
      case 'answered': return <span className="px-2 py-1 bg-blue-500/10 text-blue-500 text-xs rounded-full font-bold uppercase tracking-wider">Répondu</span>;
      case 'closed': return <span className="px-2 py-1 bg-emerald-500/10 text-emerald-500 text-xs rounded-full font-bold uppercase tracking-wider">Fermé</span>;
      default: return null;
    }
  };

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const BASE_URL = API_URL.replace('/api', '');

  return (
    <div className="flex flex-col min-h-screen bg-slate-950 text-slate-200 font-inter overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className="flex-1 p-6 md:p-10 flex flex-col relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full bg-indigo-500/5 blur-[150px] -z-10 pointer-events-none"></div>
          
          <div className="flex items-center justify-between mb-8">
             <div>
               <h1 className="text-3xl md:text-4xl font-black text-white font-outfit uppercase tracking-tight">Support Technique</h1>
               <p className="text-slate-400 font-semibold mt-1">Gérez vos requêtes et communiquez avec l'équipe</p>
             </div>
             {!activeTicket && (
               <button 
                 onClick={() => setIsModalOpen(true)}
                 className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-3 rounded-xl font-bold uppercase tracking-wider transition-colors shadow-lg shadow-indigo-500/20"
               >
                 <MessageSquarePlus size={20} />
                 <span>Nouveau Ticket</span>
               </button>
             )}
          </div>

          <div className="flex-1 glass-card border-white/5 rounded-3xl overflow-hidden flex flex-col md:flex-row relative z-10">
            {/* Tickets List */}
            <div className={`w-full md:w-1/3 border-r border-white/5 flex flex-col ${activeTicket ? 'hidden md:flex' : 'flex'}`}>
               <div className="p-4 border-b border-white/5 bg-slate-900/50">
                 <h2 className="text-sm font-black text-slate-400 uppercase tracking-widest">Vos Tickets</h2>
               </div>
               <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-2">
                  {loading ? (
                    <div className="p-8 text-center text-slate-500">Chargement...</div>
                  ) : tickets.length === 0 ? (
                    <div className="p-8 text-center text-slate-500">
                       <MessageSquarePlus size={48} className="mx-auto mb-4 opacity-20" />
                       <p>Aucun ticket trouvé</p>
                    </div>
                  ) : (
                    tickets.map(ticket => (
                      <button
                        key={ticket.id}
                        onClick={() => setActiveTicket(ticket)}
                        className={`w-full text-left p-4 rounded-2xl border transition-all ${
                          activeTicket?.id === ticket.id 
                            ? 'bg-indigo-500/10 border-indigo-500/30' 
                            : 'bg-slate-900/30 border-white/5 hover:bg-white/5'
                        }`}
                      >
                         <div className="flex justify-between items-start mb-2">
                            <span className="font-bold text-white truncate pr-2">#{ticket.id} - {ticket.subject}</span>
                            {getStatusBadge(ticket.status)}
                         </div>
                         <div className="flex justify-between items-center text-xs text-slate-500">
                            <span className="flex items-center gap-1"><Clock size={12} /> {format(new Date(ticket.updated_at), "dd MMM, HH:mm")}</span>
                            <span>{ticket.message_count} msg</span>
                         </div>
                      </button>
                    ))
                  )}
               </div>
            </div>

            {/* Chat Area */}
            {activeTicket ? (
              <div className="flex-1 flex flex-col bg-slate-900/20">
                <div className="p-4 border-b border-white/5 bg-slate-900/50 flex items-center gap-4">
                   <button onClick={() => setActiveTicket(null)} className="md:hidden text-slate-400 hover:text-white">
                     <ChevronLeft size={24} />
                   </button>
                   <div>
                     <h2 className="text-lg font-black text-white">{activeTicket.subject}</h2>
                     <p className="text-xs text-slate-500">Ticket #{activeTicket.id} • Créé le {format(new Date(activeTicket.created_at), "dd/MM/yyyy")}</p>
                   </div>
                   <div className="ml-auto">
                     {getStatusBadge(activeTicket.status)}
                   </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
                  {messages.map((msg, idx) => {
                    const isAdmin = msg.is_admin_reply;
                    return (
                      <div key={idx} className={`flex flex-col max-w-[80%] ${isAdmin ? 'self-start' : 'self-end items-end ml-auto'}`}>
                         <div className="flex items-center gap-2 mb-1">
                           <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{isAdmin ? 'Support Admin' : 'Vous'}</span>
                           <span className="text-[10px] text-slate-600">{format(new Date(msg.created_at), "dd/MM HH:mm")}</span>
                         </div>
                         <div className={`p-4 rounded-2xl ${isAdmin ? 'bg-slate-800 border border-white/5 text-slate-200 rounded-tl-none' : 'bg-indigo-600 text-white rounded-tr-none'}`}>
                           <p className="whitespace-pre-wrap">{msg.message}</p>
                           {msg.attachment_url && (
                             <div className="mt-3">
                               <a href={`${BASE_URL}${msg.attachment_url}`} target="_blank" rel="noreferrer" className="block w-full max-w-xs rounded-lg overflow-hidden border border-white/10 hover:opacity-90 transition-opacity">
                                 <img src={`${BASE_URL}${msg.attachment_url}`} alt="Attachment" className="w-full h-auto object-cover" />
                               </a>
                             </div>
                           )}
                         </div>
                      </div>
                    )
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {activeTicket.status !== 'closed' ? (
                  <div className="p-4 bg-slate-900/50 border-t border-white/5">
                    {replyFile && (
                      <div className="mb-2 flex items-center gap-2 bg-indigo-500/10 text-indigo-400 px-3 py-2 rounded-lg text-sm w-max border border-indigo-500/20">
                        <ImageIcon size={16} />
                        <span className="truncate max-w-[200px]">{replyFile.name}</span>
                        <button onClick={() => setReplyFile(null)} className="hover:text-white ml-2"><X size={16} /></button>
                      </div>
                    )}
                    <form onSubmit={handleReply} className="flex items-end gap-2">
                      <label className="cursor-pointer p-3 text-slate-400 hover:text-white bg-slate-800 rounded-xl transition-colors shrink-0">
                        <Paperclip size={20} />
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => setReplyFile(e.target.files[0])} />
                      </label>
                      <textarea
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Écrivez votre réponse..."
                        className="flex-1 bg-slate-800 border border-white/5 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 resize-none min-h-[50px] max-h-[150px] custom-scrollbar"
                        rows="1"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleReply(e);
                          }
                        }}
                      />
                      <button type="submit" disabled={!replyMessage.trim() && !replyFile} className="p-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0">
                        <Send size={20} />
                      </button>
                    </form>
                  </div>
                ) : (
                  <div className="p-4 bg-slate-900/50 border-t border-white/5 text-center text-slate-500 flex items-center justify-center gap-2">
                    <CheckCircle2 size={16} />
                    <span>Ce ticket a été clôturé par l'administration.</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden md:flex flex-1 items-center justify-center text-slate-500 bg-slate-900/20">
                <div className="text-center">
                  <MessageSquarePlus size={48} className="mx-auto mb-4 opacity-20" />
                  <p className="font-semibold uppercase tracking-widest text-xs">Sélectionnez un ticket pour voir la conversation</p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* New Ticket Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-slate-900 border border-white/10 rounded-3xl w-full max-w-lg p-6 shadow-2xl relative">
            <button onClick={() => setIsModalOpen(false)} className="absolute top-6 right-6 text-slate-400 hover:text-white">
              <X size={24} />
            </button>
            <h2 className="text-2xl font-black text-white font-outfit uppercase tracking-wider mb-6">Ouvrir un Ticket</h2>
            
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Sujet du problème</label>
                <input 
                  type="text" 
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500"
                  placeholder="Ex: Dépôt non reçu"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Message détaillé</label>
                <textarea 
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  className="w-full bg-slate-950 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500 min-h-[120px] resize-none"
                  placeholder="Décrivez votre problème en détail..."
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Capture d'écran (Optionnel)</label>
                <div className="flex items-center gap-4">
                   <label className="cursor-pointer flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white px-4 py-2 rounded-xl transition-colors text-sm font-semibold">
                     <ImageIcon size={18} />
                     <span>{selectedFile ? 'Changer l\'image' : 'Ajouter une image'}</span>
                     <input type="file" accept="image/*" className="hidden" onChange={(e) => setSelectedFile(e.target.files[0])} />
                   </label>
                   {selectedFile && <span className="text-xs text-indigo-400 truncate max-w-[200px]">{selectedFile.name}</span>}
                </div>
              </div>
              <div className="pt-4">
                <button type="submit" disabled={!newSubject.trim() || !newMessage.trim()} className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-4 rounded-xl uppercase tracking-widest transition-colors disabled:opacity-50">
                  Envoyer la demande
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
